import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApplication } from '../context/ApplicationContext';
import { SCHOOL_INFO, getSchoolPrompt } from '../config/schoolPrompts';

// Step components
import Step1Demographics from '../components/simulator/Step1Demographics';
import Step2WorkExperience from '../components/simulator/Step2WorkExperience';
import Step3Essays from '../components/simulator/Step3Essays';
import ProgressIndicator from '../components/simulator/ProgressIndicator';
import LoadingSpinner from '../components/common/LoadingSpinner';

const SimulatorPage = () => {
  const { school } = useParams();
  const navigate = useNavigate();
  const { 
    selectedSchool, 
    currentStep, 
    setSchool, 
    isLoading,
    loadDemoData 
  } = useApplication();

  // Set school from URL parameter
  useEffect(() => {
    if (school) {
      const schoolKey = school.toUpperCase();
      const schoolInfo = SCHOOL_INFO[schoolKey];
      if (schoolInfo) {
        const schoolData = {
          value: schoolKey,
          label: schoolInfo.name,
          description: schoolInfo.description
        };
        setSchool(schoolData);
      } else {
        navigate('/');
      }
    } else {
      // If no school in URL, redirect to homepage for school selection
      navigate('/');
    }
  }, [school, navigate]); // Removed setSchool from dependency array to prevent infinite loop

  // Show loading while school is being set
  if (!selectedSchool) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Get school prompt data
  const schoolPrompt = getSchoolPrompt(selectedSchool.value);

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1Demographics schoolPrompt={schoolPrompt} />;
      case 2:
        return <Step2WorkExperience schoolPrompt={schoolPrompt} />;
      case 3:
        return <Step3Essays schoolPrompt={schoolPrompt} />;
      default:
        return <Step1Demographics schoolPrompt={schoolPrompt} />;
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8 relative">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            MBA Evaluation for{' '}
            <span className="gradient-text">{selectedSchool.label}</span>
          </h1>
          <p className="text-gray-600 text-lg">
            {selectedSchool.description}
          </p>
          
          {/* Demo Mode Button - For Testing */}
          <button
            onClick={loadDemoData}
            className="absolute top-0 right-0 px-3 py-1 text-xs bg-yellow-100 text-yellow-800 rounded border border-yellow-300 hover:bg-yellow-200 transition-colors"
            title="Load demo data for testing"
          >
            🧪 Demo Mode
          </button>
        </div>

        {/* Progress Indicator */}
        <ProgressIndicator />

        {/* Loading Overlay */}
        {isLoading && <LoadingSpinner step={currentStep} />}

        {/* Current Step Content */}
        <div className="mt-8">
          {renderCurrentStep()}
        </div>
      </div>
    </div>
  );
};

export default SimulatorPage; 